import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Progress } from '../../components/ui/progress';
import {
  User, Building2, CreditCard, FileSignature, Lock,
  ChevronRight, ChevronLeft, Check, AlertCircle, Loader2,
  Phone, Mail, MapPin, Briefcase, Eye, EyeOff, X, Camera,
  Upload, Info, LogOut
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

const STEPS = {
  PROFILE: 'profile',
  COMPANY: 'company',
  BANK: 'bank',
  CONTRACT: 'contract',
  PASSWORD: 'password'
};

const OnboardingModal = ({ isOpen, onComplete, onClose }) => {
  const { user, refreshUser, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contract, setContract] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar_url || null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  
  // Determine which steps are needed based on user role
  const isAffiliate = user?.role === 'affiliate';
  const isTeamMember = ['team_member', 'creative', 'project_manager', 'customer_service'].includes(user?.role);
  const isClient = user?.role === 'client';
  const needsContract = isAffiliate || isTeamMember;
  
  // Form data
  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    address: user?.address || '',
    postal_code: user?.postal_code || '',
    city: user?.city || '',
    bio: user?.bio || ''
  });
  
  const [companyData, setCompanyData] = useState({
    company_name: user?.company_name || '',
    siret: user?.siret || '',
    tva_number: user?.tva_number || '',
    legal_status: user?.legal_status || '',
    company_address: user?.company_address || '',
    company_postal_code: user?.company_postal_code || '',
    company_city: user?.company_city || ''
  });
  
  const [bankData, setBankData] = useState({
    iban: user?.iban || '',
    bic: user?.bic || '',
    bank_name: user?.bank_name || '',
    account_holder: user?.account_holder || `${user?.first_name || ''} ${user?.last_name || ''}`.trim()
  });
  
  const [contractData, setContractData] = useState({
    accepted_terms: false,
    accepted_cgv: false
  });
  
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: ''
  });

  // Build steps array based on user role
  const steps = React.useMemo(() => {
    const allSteps = [
      { id: STEPS.PROFILE, label: 'Profil', icon: User, required: true },
      { id: STEPS.COMPANY, label: 'Entreprise', icon: Building2, required: isAffiliate || isTeamMember },
      { id: STEPS.BANK, label: 'RIB', icon: CreditCard, required: isAffiliate || isTeamMember },
      { id: STEPS.CONTRACT, label: 'Contrat', icon: FileSignature, required: needsContract },
      { id: STEPS.PASSWORD, label: 'Mot de passe', icon: Lock, required: true }
    ];
    return allSteps.filter(step => step.required);
  }, [isAffiliate, isTeamMember, needsContract]);

  useEffect(() => {
    if (needsContract && isOpen) {
      fetchContract();
    }
  }, [needsContract, isOpen]);

  const fetchContract = async () => {
    try {
      // Fetch pending contract for this user
      const res = await api.get('/member-contracts/my-contracts');
      const pendingContract = res.data?.find(c => 
        c.status === 'pending_signature' || c.status === 'pending_member'
      );
      setContract(pendingContract);
    } catch (error) {
      console.log('No pending contract found');
    }
  };

  // Handle avatar file selection
  const handleAvatarSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 5 Mo');
      return;
    }
    
    setAvatarFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setAvatarPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Upload avatar
  const uploadAvatar = async () => {
    if (!avatarFile) return null;
    
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', avatarFile);
      
      const res = await api.post('/users/me/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      return res.data.avatar_url;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Erreur lors de l\'upload de la photo');
      return null;
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Handle close/later
  const handleCloseLater = () => {
    toast.info('Vous pourrez compléter votre profil lors de votre prochaine connexion');
    if (onClose) onClose();
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  const validateCurrentStep = () => {
    switch (currentStepData?.id) {
      case STEPS.PROFILE:
        if (!profileData.first_name || !profileData.last_name) {
          toast.error('Prénom et nom sont obligatoires');
          return false;
        }
        if (!profileData.phone) {
          toast.error('Le numéro de téléphone est obligatoire');
          return false;
        }
        return true;
        
      case STEPS.COMPANY:
        // Company is optional for some, but if filled should be complete
        if (companyData.siret && companyData.siret.length !== 14) {
          toast.error('Le SIRET doit contenir 14 chiffres');
          return false;
        }
        return true;
        
      case STEPS.BANK:
        if (!bankData.iban) {
          toast.error('L\'IBAN est obligatoire pour recevoir vos paiements');
          return false;
        }
        if (bankData.iban.length < 15) {
          toast.error('L\'IBAN semble invalide');
          return false;
        }
        return true;
        
      case STEPS.CONTRACT:
        if (!contractData.accepted_terms) {
          toast.error('Veuillez accepter les conditions générales');
          return false;
        }
        return true;
        
      case STEPS.PASSWORD:
        if (!passwordData.new_password) {
          toast.error('Veuillez saisir un nouveau mot de passe');
          return false;
        }
        if (passwordData.new_password.length < 6) {
          toast.error('Le mot de passe doit contenir au moins 6 caractères');
          return false;
        }
        if (passwordData.new_password !== passwordData.confirm_password) {
          toast.error('Les mots de passe ne correspondent pas');
          return false;
        }
        return true;
        
      default:
        return true;
    }
  };

  const saveCurrentStep = async () => {
    setSaving(true);
    try {
      switch (currentStepData?.id) {
        case STEPS.PROFILE:
          // Upload avatar if selected
          if (avatarFile) {
            await uploadAvatar();
          }
          await api.put('/users/me', profileData);
          break;
          
        case STEPS.COMPANY:
          await api.put('/users/me', companyData);
          break;
          
        case STEPS.BANK:
          await api.put('/users/me', {
            ...bankData,
            bank_info_completed: true
          });
          break;
          
        case STEPS.CONTRACT:
          if (contract) {
            await api.post(`/member-contracts/${contract.id}/sign`);
          }
          await api.put('/users/me', { contract_signed: true });
          break;
          
        case STEPS.PASSWORD:
          await api.put('/users/me/set-password', {
            new_password: passwordData.new_password
          });
          await api.put('/users/me', { 
            password_changed: true,
            onboarding_completed: true 
          });
          break;
      }
      return true;
    } catch (error) {
      console.error('Error saving step:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (!validateCurrentStep()) return;
    
    const saved = await saveCurrentStep();
    if (!saved) return;
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Onboarding complete
      toast.success('🎉 Bienvenue ! Votre profil est maintenant complet.');
      if (refreshUser) await refreshUser();
      onComplete?.();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const renderStepContent = () => {
    switch (currentStepData?.id) {
      case STEPS.PROFILE:
        return (
          <div className="space-y-3 sm:space-y-4">
            {/* Avatar Upload Section */}
            <div className="flex flex-col sm:flex-row gap-4 p-3 sm:p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-start gap-3 sm:gap-4">
                {/* Avatar Preview */}
                <div className="relative">
                  <div 
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden border-2 border-slate-200 bg-white flex items-center justify-center cursor-pointer hover:border-slate-300 transition-all"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {avatarPreview ? (
                      <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex flex-col items-center text-slate-400">
                        <Camera className="w-6 h-6 sm:w-8 sm:h-8" />
                        <span className="text-[10px] sm:text-xs mt-1">Ajouter</span>
                      </div>
                    )}
                  </div>
                  {uploadingAvatar && (
                    <div className="absolute inset-0 bg-white/80 rounded-full flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarSelect}
                    className="hidden"
                  />
                </div>
                
                {/* Example Photo */}
                <div className="hidden sm:block">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden border-2 border-dashed border-slate-300 bg-slate-100">
                    <img 
                      src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face" 
                      alt="Exemple" 
                      className="w-full h-full object-cover opacity-70"
                    />
                  </div>
                  <p className="text-[10px] text-slate-400 text-center mt-1">Exemple</p>
                </div>
              </div>
              
              {/* Photo Tips */}
              <div className="flex-1 space-y-1 sm:space-y-2">
                <Label className="text-xs sm:text-sm font-medium flex items-center gap-1">
                  <Info className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500" />
                  Conseils pour votre photo
                </Label>
                <ul className="text-[10px] sm:text-xs text-slate-600 space-y-0.5 sm:space-y-1">
                  <li className="flex items-start gap-1">
                    <Check className="w-3 h-3 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Photo de type identité, visage bien visible</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <Check className="w-3 h-3 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Fond neutre (uni de préférence)</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <Check className="w-3 h-3 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Bonne luminosité, regard vers l'objectif</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <X className="w-3 h-3 text-red-500 flex-shrink-0 mt-0.5" />
                    <span>Évitez les photos de groupe ou floues</span>
                  </li>
                </ul>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-2 h-7 sm:h-8 text-xs"
                >
                  <Upload className="w-3 h-3 mr-1" />
                  {avatarPreview ? 'Changer la photo' : 'Choisir une photo'}
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-xs sm:text-sm">Prénom *</Label>
                <Input
                  value={profileData.first_name}
                  onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                  placeholder="Votre prénom"
                  className="h-9 sm:h-10 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs sm:text-sm">Nom *</Label>
                <Input
                  value={profileData.last_name}
                  onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                  placeholder="Votre nom"
                  className="h-9 sm:h-10 text-sm"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs sm:text-sm">Téléphone *</Label>
              <Input
                value={profileData.phone}
                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                placeholder="06 12 34 56 78"
                className="h-9 sm:h-10 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs sm:text-sm">Adresse</Label>
              <Input
                value={profileData.address}
                onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                placeholder="Numéro et rue"
                className="h-9 sm:h-10 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-xs sm:text-sm">Code postal</Label>
                <Input
                  value={profileData.postal_code}
                  onChange={(e) => setProfileData({ ...profileData, postal_code: e.target.value })}
                  placeholder="13800"
                  className="h-9 sm:h-10 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs sm:text-sm">Ville</Label>
                <Input
                  value={profileData.city}
                  onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
                  placeholder="Istres"
                  className="h-9 sm:h-10 text-sm"
                />
              </div>
            </div>
          </div>
        );

      case STEPS.COMPANY:
        return (
          <div className="space-y-3 sm:space-y-4">
            <div className="p-2 sm:p-3 bg-blue-50 rounded-lg text-xs sm:text-sm text-blue-700 mb-2 sm:mb-4">
              <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
              Ces informations sont nécessaires pour la facturation
            </div>
            <div>
              <Label className="text-xs sm:text-sm">Raison sociale / Nom entreprise</Label>
              <Input
                value={companyData.company_name}
                onChange={(e) => setCompanyData({ ...companyData, company_name: e.target.value })}
                placeholder="Ma Société SARL"
                className="h-9 sm:h-10 text-sm"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-xs sm:text-sm">SIRET (14 chiffres)</Label>
                <Input
                  value={companyData.siret}
                  onChange={(e) => setCompanyData({ ...companyData, siret: e.target.value.replace(/\D/g, '').slice(0, 14) })}
                  placeholder="12345678901234"
                  maxLength={14}
                  className="h-9 sm:h-10 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs sm:text-sm">N° TVA intracommunautaire</Label>
                <Input
                  value={companyData.tva_number}
                  onChange={(e) => setCompanyData({ ...companyData, tva_number: e.target.value })}
                  placeholder="FR12345678901"
                  className="h-9 sm:h-10 text-sm"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs sm:text-sm">Statut juridique</Label>
              <Input
                value={companyData.legal_status}
                onChange={(e) => setCompanyData({ ...companyData, legal_status: e.target.value })}
                placeholder="Auto-entrepreneur, SARL, SAS..."
                className="h-9 sm:h-10 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs sm:text-sm">Adresse de l'entreprise</Label>
              <Input
                value={companyData.company_address}
                onChange={(e) => setCompanyData({ ...companyData, company_address: e.target.value })}
                placeholder="Adresse du siège"
                className="h-9 sm:h-10 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-xs sm:text-sm">Code postal</Label>
                <Input
                  value={companyData.company_postal_code}
                  onChange={(e) => setCompanyData({ ...companyData, company_postal_code: e.target.value })}
                  placeholder="13800"
                  className="h-9 sm:h-10 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs sm:text-sm">Ville</Label>
                <Input
                  value={companyData.company_city}
                  onChange={(e) => setCompanyData({ ...companyData, company_city: e.target.value })}
                  placeholder="Istres"
                  className="h-9 sm:h-10 text-sm"
                />
              </div>
            </div>
          </div>
        );

      case STEPS.BANK:
        return (
          <div className="space-y-3 sm:space-y-4">
            <div className="p-2 sm:p-3 bg-amber-50 rounded-lg text-xs sm:text-sm text-amber-700 mb-2 sm:mb-4">
              <CreditCard className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
              Ces informations sont nécessaires pour recevoir vos paiements
            </div>
            <div>
              <Label className="text-xs sm:text-sm">Titulaire du compte *</Label>
              <Input
                value={bankData.account_holder}
                onChange={(e) => setBankData({ ...bankData, account_holder: e.target.value })}
                placeholder="Nom et prénom ou raison sociale"
                className="h-9 sm:h-10 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs sm:text-sm">IBAN *</Label>
              <Input
                value={bankData.iban}
                onChange={(e) => setBankData({ ...bankData, iban: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                placeholder="FR76 1234 5678 9012 3456 7890 123"
                className="font-mono h-9 sm:h-10 text-xs sm:text-sm"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-xs sm:text-sm">BIC / SWIFT</Label>
                <Input
                  value={bankData.bic}
                  onChange={(e) => setBankData({ ...bankData, bic: e.target.value.toUpperCase() })}
                  placeholder="BNPAFRPP"
                  className="font-mono h-9 sm:h-10 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs sm:text-sm">Nom de la banque</Label>
                <Input
                  value={bankData.bank_name}
                  onChange={(e) => setBankData({ ...bankData, bank_name: e.target.value })}
                  placeholder="BNP Paribas"
                  className="h-9 sm:h-10 text-sm"
                />
              </div>
            </div>
          </div>
        );

      case STEPS.CONTRACT:
        return (
          <div className="space-y-4">
            {contract ? (
              <>
                <div className="p-4 bg-slate-50 rounded-lg border">
                  <h4 className="font-semibold mb-2">{contract.title || 'Contrat de collaboration'}</h4>
                  <p className="text-sm text-slate-600 mb-3">
                    Veuillez lire et accepter les conditions du contrat pour continuer.
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.open(`/member-contracts/${contract.id}`, '_blank')}
                  >
                    <FileSignature className="w-4 h-4 mr-2" />
                    Voir le contrat complet
                  </Button>
                </div>
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <Checkbox 
                    id="terms" 
                    checked={contractData.accepted_terms}
                    onCheckedChange={(checked) => setContractData({ ...contractData, accepted_terms: checked })}
                  />
                  <label htmlFor="terms" className="text-sm cursor-pointer">
                    J'ai lu et j'accepte les termes du contrat de collaboration. Je m'engage à respecter les conditions définies.
                  </label>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <FileSignature className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucun contrat en attente de signature</p>
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg mt-4">
                  <Checkbox 
                    id="terms-no-contract" 
                    checked={contractData.accepted_terms}
                    onCheckedChange={(checked) => setContractData({ ...contractData, accepted_terms: checked })}
                  />
                  <label htmlFor="terms-no-contract" className="text-sm cursor-pointer">
                    J'accepte les conditions générales d'utilisation de la plateforme
                  </label>
                </div>
              </div>
            )}
          </div>
        );

      case STEPS.PASSWORD:
        return (
          <div className="space-y-4">
            <div className="p-3 bg-emerald-50 rounded-lg text-sm text-emerald-700 mb-4">
              <Lock className="w-4 h-4 inline mr-2" />
              Pour votre sécurité, veuillez définir un nouveau mot de passe
            </div>
            <div>
              <Label>Nouveau mot de passe *</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  placeholder="Minimum 6 caractères"
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>Confirmer le mot de passe *</Label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  placeholder="Retapez votre mot de passe"
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {passwordData.new_password && (
              <div className="text-sm">
                <p className={cn(
                  "flex items-center gap-2",
                  passwordData.new_password.length >= 6 ? "text-emerald-600" : "text-slate-400"
                )}>
                  {passwordData.new_password.length >= 6 ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  Au moins 6 caractères
                </p>
                <p className={cn(
                  "flex items-center gap-2",
                  passwordData.new_password === passwordData.confirm_password && passwordData.confirm_password ? "text-emerald-600" : "text-slate-400"
                )}>
                  {passwordData.new_password === passwordData.confirm_password && passwordData.confirm_password ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  Les mots de passe correspondent
                </p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="w-[95vw] max-w-2xl max-h-[85vh] overflow-y-auto p-4 sm:p-6" 
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl" style={{ color: theme.colors?.primary }}>
            <User className="w-4 h-4 sm:w-5 sm:h-5" />
            Bienvenue ! Complétez votre profil
          </DialogTitle>
          <DialogDescription>
            Avant de commencer, quelques informations sont nécessaires
          </DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="space-y-2 sm:space-y-3">
          <div className="flex justify-between text-xs sm:text-sm">
            <span className="font-medium">{currentStepData?.label}</span>
            <span className="text-slate-500">Étape {currentStep + 1}/{steps.length}</span>
          </div>
          <Progress value={progress} className="h-1.5 sm:h-2" />
          
          {/* Step indicators - hidden on very small screens, simplified on mobile */}
          <div className="hidden xs:flex justify-between gap-1">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              return (
                <div 
                  key={step.id}
                  className={cn(
                    "flex flex-col items-center gap-0.5 sm:gap-1 flex-1",
                    isActive ? "text-slate-900" : isCompleted ? "text-emerald-600" : "text-slate-300"
                  )}
                >
                  <div className={cn(
                    "w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-2 transition-all",
                    isActive ? "border-current bg-slate-100" : 
                    isCompleted ? "border-emerald-500 bg-emerald-50" : "border-slate-200"
                  )}>
                    {isCompleted ? (
                      <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                    ) : (
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                    )}
                  </div>
                  <span className="text-[10px] sm:text-xs hidden sm:block text-center">{step.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Step content */}
        <div className="py-2 sm:py-4">
          {renderStepContent()}
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between pt-3 sm:pt-4 border-t gap-2 sm:gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevious}
            disabled={currentStep === 0 || saving}
            className="text-xs sm:text-sm px-2 sm:px-4"
          >
            <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden xs:inline">Précédent</span>
            <span className="xs:hidden">Retour</span>
          </Button>
          
          <Button
            onClick={handleNext}
            disabled={saving}
            size="sm"
            style={{ backgroundColor: theme.colors?.accent }}
            className="text-xs sm:text-sm px-3 sm:px-4"
          >
            {saving ? (
              <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 animate-spin" />
            ) : currentStep === steps.length - 1 ? (
              <Check className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            ) : (
              <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            )}
            {currentStep === steps.length - 1 ? 'Terminer' : 'Suivant'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;
